import React, { memo, useMemo } from 'react';
import { cn } from '@/shared/utils/cn';
import { LinearGraphicProps } from '../../types';
import { LinearGraphicSegment } from '../atoms';
import { ListWithDataInHover } from '@/modules/ListWithDataInHover';
import { ListWithDataInHoverColor } from '@/modules/ListWithDataInHover/types';
import { DEFAULT_COLORS, normalizeColor } from '../../utils/colors';

/**
 * LinearGraphic - Gráfica lineal de barras de progreso múltiples
 *
 * Muestra una barra de progreso con múltiples segmentos, cada uno con su propio
 * color y porcentaje. Soporta tooltips en hover para mostrar información detallada.
 *
 * @example
 * ```tsx
 * <LinearGraphic
 *   data={[
 *     { name: 'Completado', percent: 60 },
 *     { name: 'En progreso', percent: 30 },
 *     { name: 'Pendiente', percent: 10 }
 *   ]}
 *   colors={['success', 'warning', 'neutral']}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Con colores semánticos del sistema
 * <LinearGraphic
 *   data={[
 *     { name: 'Éxito', percent: 70 },
 *     { name: 'Advertencia', percent: 20 },
 *     { name: 'Error', percent: 10 }
 *   ]}
 *   colors={['success', 'warning', 'danger']}
 *   height={12}
 *   showTooltips={true}
 * />
 * ```
 */
export const LinearGraphic = memo(
  ({
    data,
    colors,
    className,
    height = 8,
    showTooltips = true,
    showLegend = false,
    showLegendPercent = false,
    legendGap = 8,
    transitionDuration = 500,
    disabled = false,
    onSegmentHover,
    onSegmentLeave,
  }: LinearGraphicProps) => {
    // Validar y normalizar datos
    const normalizedData = useMemo(() => {
      if (!Array.isArray(data) || data.length === 0) {
        return [];
      }

      return data
        .filter((item) => item !== null && typeof item === 'object')
        .map((item) => ({
          name: String(item.name ?? ''),
          percent: Math.max(0, Math.min(100, Number(item.percent) || 0)),
        }))
        .filter((item) => item.percent > 0); // Filtrar segmentos con 0%
    }, [data]);

    // Validar y normalizar colores semánticos
    const normalizedColors = useMemo(() => {
      if (!Array.isArray(colors) || colors.length === 0) {
        return DEFAULT_COLORS;
      }

      return colors.map((color) => normalizeColor(color));
    }, [colors]);

    // Calcular el total de porcentajes para normalizar si excede 100%
    const totalPercent = useMemo(() => {
      return normalizedData.reduce((sum, item) => sum + item.percent, 0);
    }, [normalizedData]);

    // Normalizar porcentajes si el total excede 100%
    const normalizedPercentages = useMemo(() => {
      if (totalPercent === 0) {
        return normalizedData.map(() => 0);
      }

      if (totalPercent > 100) {
        // Escalar proporcionalmente
        return normalizedData.map((item) => (item.percent / totalPercent) * 100);
      }

      return normalizedData.map((item) => item.percent);
    }, [normalizedData, totalPercent]);

    // Clases del contenedor - sin overflow-hidden para que el tooltip sea visible
    const containerClasses = cn(
      'ui-relative ui-w-full',
      disabled && 'ui-opacity-50 ui-pointer-events-none',
      className,
    );

    // Estilos del contenedor
    const containerStyle: React.CSSProperties = {
      height: `${height}px`,
      borderRadius: '8px',
      backgroundColor: 'var(--ui-color-neutral-200-legacy, #D9D9D9)',
      display: 'flex',
      alignItems: 'stretch',
    };

    // Si no hay datos, retornar null o un estado vacío
    if (normalizedData.length === 0) {
      return (
        <div className="ui-imj ui-flex ui-w-full ui-flex-col ui-gap-8">
          <div
            className={containerClasses}
            style={containerStyle}
            role="progressbar"
            aria-valuenow={0}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Gráfica lineal vacía"
          />
        </div>
      );
    }

    return (
      <div className="ui-flex ui-w-full ui-flex-col ui-gap-8">
        {/* Barra de progreso */}
        <div
          className={containerClasses}
          style={containerStyle}
          role="progressbar"
          aria-valuenow={totalPercent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Gráfica lineal de progreso"
        >
          {normalizedData.map((item, index) => {
            const color = normalizedColors[index % normalizedColors.length];
            const width = normalizedPercentages[index];

            return (
              <LinearGraphicSegment
                key={`linear-graphic-segment-${index}-${item.name}`}
                data={item}
                index={index}
                width={width}
                color={color}
                isFirst={index === 0}
                isLast={index === normalizedData.length - 1}
                height={height}
                transitionDuration={transitionDuration}
                showTooltip={showTooltips}
                onHover={onSegmentHover}
                onLeave={onSegmentLeave}
              />
            );
          })}
        </div>

        {/* Leyenda usando ListWithDataInHover */}
        {showLegend && (
          <ListWithDataInHover
            data={normalizedData}
            colors={normalizedColors as ListWithDataInHoverColor[]}
            gap={legendGap}
            showTooltips={true}
            showPercent={showLegendPercent}
            tooltipPosition="over"
          />
        )}
      </div>
    );
  },
);

LinearGraphic.displayName = 'LinearGraphic';
